<script lang="tsx">
import { computed, defineComponent, ref, type PropType } from 'vue'
import { createNamespace, friendBytes, noop } from '@bd/vue3-utils'
import { FileOutlined } from '@ant-design/icons-vue'
import { Button, Table, type TableColumnType, type TableProps } from 'ant-design-vue'
import { ConditionBox } from '-/Base/ConditionBox'
import { Tag } from '-/Common/Tag'
import { API_FILE_DEL, API_FILE_LIST } from '@/api/file'
import { useTable } from '@/hooks/useTable'
import { delData } from '@/hooks/useDel'
import UpdateModal from './UpdateModal.vue'
import { CONDITION_CONFIG, DATA_INDEX } from './constant'
import { FileDisplayDetailDrawer, handleDownload } from '-/Common/FileDisplay'
import FileIdName from '../FileIdName.vue'
import { useImagePreview } from '@/hooks/useImagePreview'

const [name, bem] = createNamespace('OssList')

export default defineComponent({
  name,
  props: {
    value: {
      type: Array as PropType<ApiFile.Item[]>,
      default: () => []
    }
  },
  emits: ['update:value'],
  setup(props, { emit }) {
    const updateModalRef = ref<InstanceType<typeof UpdateModal> | null>(null)

    const {
      pagination,
      loading,
      dataSource,
      refresh: fetchData,
      handleConditionChange,
      handleTableChange
    } = useTable<ApiFile.Item>({
      conditionConfig: CONDITION_CONFIG,
      api: API_FILE_LIST
    })

    /** 从“已选择文件列表”中移除指定 id */
    const removeFromSelected = (id: string) => {
      const items = props.value.filter(item => item.id !== id)
      emit('update:value', items)
    }

    /** 点击删除 */
    const handleDel = async (id: string, fileName?: string) => {
      try {
        await delData(API_FILE_DEL, { ids: [id] }, fileName)
        removeFromSelected(id)
        fetchData()
      } catch (err) {
        noop()
      }
    }
    const detailDrawerRef = ref()
    const { preview } = useImagePreview()
    /** 构建表格列（单一职责：只负责列定义） */
    const columns: TableColumnType<ApiFile.Item>[] = [
      {
        title: 'ID/文件名',
        dataIndex: DATA_INDEX.FILE_NAME,
        width: 200,
        fixed: 'left',
        sorter: true,
        customRender({ record }) {
          return (
            <FileIdName
              record={record}
              onDetail={() => detailDrawerRef.value?.open(record)}
              onDownload={() => handleDownload(record.downloadUrl)}
              onPreview={() => preview(record.downloadUrl)}
            />
          )
        }
      },
      {
        title: '文件展示名',
        dataIndex: DATA_INDEX.FILE_SHOW_NAME,
        ellipsis: true,
        width: 200
      },
      {
        title: '文件编号',
        dataIndex: DATA_INDEX.FILE_CODE_NUMBER,
        width: 200
      },
      {
        title: '文件大小',
        dataIndex: DATA_INDEX.FILE_SIZE,
        width: 100,
        sorter: true,
        customRender({ text = 0 }) {
          return friendBytes(text)
        }
      },
      {
        title: '文件时间',
        dataIndex: DATA_INDEX.FILE_SHOW_TIME,
        width: 180,
        sorter: true
      },
      {
        title: '操作',
        dataIndex: 'operate',
        width: 120,
        fixed: 'right',
        customRender: ({ record }) => (
          <>
            <Button
              type='link'
              size='small'
              onClick={e => {
                e.stopPropagation()
                updateModalRef.value?.open(record)
              }}
            >
              编辑
            </Button>
            <Button
              type='link'
              size='small'
              danger
              onClick={e => {
                e.stopPropagation()
                handleDel(record.id!, record.fileShowName)
              }}
            >
              删除
            </Button>
          </>
        )
      }
    ]

    /** 已选中的 key Set，用于 O(1) 查找 */
    const selectedKeySet = computed(() => new Set(props.value.map(item => item.id!)))

    /** 已选中的 key 数组，用于 Table rowSelection */
    const selectedRowKeys = computed(() => [...selectedKeySet.value])

    /** rowSelection 公共配置 */
    const rowSelectionCommon: TableProps['rowSelection'] = {
      preserveSelectedRowKeys: true,
      type: 'checkbox',
      onChange(keys, rows) {
        // 当 preserveSelectedRowKeys 为 true 时，rows 中不在当前 dataSource 的项会是 undefined
        // 需要根据 keys 把 undefined 的项用 props.value 中对应的数据补全
        const valueMap = new Map(props.value.map(item => [item.id, item]))
        const items = keys
          .map((key, index) => {
            // 优先使用 rows 中的数据（当前页的数据）
            return rows[index] ?? valueMap.get(key as string)
          })
          .filter(Boolean) as ApiFile.Item[]

        emit('update:value', items)
      }
    }

    /** 渲染底部已选文件 */
    const renderSelectedFooter = () => (
      <div class={bem('ft')}>
        <div class={bem('tip')}>已选：{props.value.length} 个文件</div>
        <div class={bem('files')}>
          {props.value.map(item => (
            <Tag key={item.id} closable icon={<FileOutlined />} onClose={() => removeFromSelected(item.id!)}>
              {item.fileShowName}
            </Tag>
          ))}
        </div>
      </div>
    )

    const customRow = (record: ApiFile.Item) => {
      return {
        onClick: () => {
          if (selectedKeySet.value.has(record.id!)) {
            // 已选中，取消选中
            removeFromSelected(record.id!)
          } else {
            // 未选中，添加选中
            emit('update:value', [...props.value, record])
          }
        }
      }
    }
    return () => (
      <div class={bem()}>
        <ConditionBox config={CONDITION_CONFIG} onChange={handleConditionChange} />

        <div class={bem('bd')}>
          <Table
            columns={columns}
            rowKey='id'
            scroll={{ x: 'max-content' }}
            rowSelection={{
              ...rowSelectionCommon,
              selectedRowKeys: selectedRowKeys.value
            }}
            customRow={customRow}
            loading={loading.value}
            size='small'
            dataSource={dataSource.value}
            pagination={pagination.value}
            onChange={handleTableChange}
          />
        </div>

        {renderSelectedFooter()}

        <UpdateModal ref={updateModalRef} onRefresh={fetchData} />
        <FileDisplayDetailDrawer ref={detailDrawerRef} />
      </div>
    )
  }
})
</script>

<style lang="less">
.oss-list {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 135px);

  &__bd {
    flex: 1;
    overflow-y: auto;
  }

  &__ft {
    padding-top: @padding-md;
    border-top: @border-base;
  }

  &__files {
    display: flex;
    flex-wrap: wrap;
    gap: @margin-xss;
    max-height: 108px;
    overflow-y: auto;
    // 优化大量Tag渲染性能，跳过不可见区域的渲染
    content-visibility: auto;
    contain-intrinsic-size: auto 108px;

    .ant-tag {
      margin: 0;
    }
  }

  &__tip {
    margin-bottom: @margin-sm;
  }
}
</style>
